/**
 * The composer, shared by Home and a chat — the prototype's one input.
 *
 * Top to bottom: a question the last reply asked (`QuestionDock`, a chat
 * only), the part of a reply being answered (picked with Quote), the files
 * being attached, the auto-growing text box with the Tab
 * badge when a suggested reply is waiting, then the tool row — +, the project
 * pill (Home only), the model, its effort where it offers one, the context
 * ring (a chat only), the microphone and send. Send turns into stop while a
 * reply streams.
 *
 * On a phone the row tightens: the project pill is its folder alone, and the
 * model's chip carries the effort too, with the slider at the foot of its
 * menu. Under a finger the microphone and send grow to 40px, and the
 * suggested reply is a chip to tap, since there is no Tab to press.
 *
 * Files upload the moment they are picked, pasted or dropped on the box, so
 * sending does not wait on a large one unless it has not finished; the chips
 * show which are still going. An image the model will not see says so once,
 * under them.
 * The page decides what sending means (Home makes a chat first) and gets text
 * plus stored attachments; the draft clears only once that has not thrown.
 *
 * A finished slash command leaves the text and becomes a chip in front of it
 * (`slash.ts`); Backspace at the start of the text takes it off again. The
 * menu offers the built-in commands, then the person's skills that are on.
 * Escape or a click elsewhere shuts it until the text changes.
 *
 * The text box is a small editor (`editor.ts`) that turns backticks into
 * inline code and code blocks as they are typed. What it hands this file is
 * the same Markdown text a textarea would hold, so the draft stays a string.
 *
 * Hidden until they work, rather than drawn and dead: the Auto/Manual mode
 * menu (it governs connectors, and the web app has none yet), "From Cloud",
 * and `/compose`. `/schedule` shows where an automation can be made: a plan
 * that has them, a model that uses tools, a chat that is not temporary.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BUILTIN_SKILLS, MAX_AUTOMATIONS, MAX_FILE_BYTES, MAX_VIDEOS_PER_DAY } from '@wire';
import { ApiError, deleteFile, uploadFile, type Attachment, type ContextUse, type Effort } from '../../api';
import { useDictation } from '../../hooks/useDictation';
import { takeQueued, useQueued } from '../../hooks/useTurns';
import { joinText } from '../../audio/dictationText';
import { useRouter } from '../../router';
import { desktopTools, inDesktop, onDesktopChange } from '../../desktop';
import { useApp } from '../../state';
import { AttachmentChip } from '../Attachments';
import { QuoteLines } from '../MessageList';
import { useShell } from '../Shell';
import type { PendingAsk } from '../pendingAsk';
import { QuestionDock } from '../QuestionDock';
import { Ph, useDismiss } from '../ui';
import { AttachMenu } from './AttachMenu';
import { CommandChip } from './CommandChip';
import { createEditor, type ComposerEditor } from './editor';
import { imageNotice } from './images';
import { pcNotice, computerNotice, offersComputer } from './computer';
import { EffortMenu } from './EffortMenu';
import { ModelMenu } from './ModelMenu';
import { ProjectPill } from './ProjectPill';
import { commandText, GROUP_ICON, leadingCommand, type SlashCommand } from './slash';
import { UsagePopover, UsageRing } from './Usage';

const HOME_PROMPTS = [
  'What are we working on?',
  'What should we make today?',
  'Where do you want to start?',
  'What can I take off your plate?'
];

const SLASH: {
  name: SlashCommand;
  icon: string;
  label: string;
  placeholder: string;
  needs: 'image' | 'video' | 'automations' | null;
}[] = [
  { name: 'image', icon: 'image', label: 'Create an image', placeholder: 'Describe the image', needs: 'image' },
  { name: 'video', icon: 'video-camera', label: 'Create a video', placeholder: 'Describe the video', needs: 'video' },
  {
    name: 'summarise',
    icon: 'text-align-left',
    label: 'Summarise a file or chat',
    placeholder: 'What to summarise — or leave it empty for this chat',
    needs: null
  },
  { name: 'remember', icon: 'brain', label: 'Save to memory', placeholder: 'What should Assist remember?', needs: null },
  { name: 'schedule', icon: 'clock-countdown', label: 'Run on a schedule', placeholder: 'What to do, and when', needs: 'automations' }
];

/** What a skill's chip asks for. It runs with nothing after it too. */
const SKILL_PLACEHOLDER = 'Add details, or send it as it is';

/** The tallest the text box grows before it scrolls. */
const MAX_INPUT = 240;
/** One line of the text box: 15px at a line height of 1.5. */
const LINE = 22.5;

interface Pending {
  key: string;
  name: string;
  mime: string;
  state: 'uploading' | 'ready' | 'failed';
  error?: string;
  fileId: string | null;
  done: Promise<Attachment | null>;
}

type MenuName = 'attach' | 'model' | 'effort' | 'project' | 'usage';

let keySeq = 0;

export interface ComposerProps {
  variant: 'home' | 'chat';
  model: string;
  onModelChange: (id: string) => void;
  /** Shown only where the model offers a choice. */
  effort: Effort;
  onEffortChange: (effort: Effort) => void;
  /** Home only. */
  projectId?: string | null;
  onProjectChange?: (id: string | null) => void;
  /** A chat only: fills the context ring. */
  context?: ContextUse | null;
  /** A reply the person might send next; Tab takes it. */
  suggestion?: string | null;
  /** A chat only: the questions the last reply asked. The dock's answers go
   *  to `onAnswer`; the page makes every send while they wait the answer. */
  question?: PendingAsk | null;
  onAnswer?: (picks: string[]) => Promise<void> | void;
  /** A reply is streaming: stop sits beside send, and what is sent waits in
   *  the chat's queue (hooks/useTurns.ts) for the reply to end. */
  busy?: boolean;
  onStop?: () => void;
  /** Stop the reply and send what is queued at once. */
  onSendNow?: () => void;
  onSend: (text: string, attachments: Attachment[]) => Promise<void> | void;
  /** Uploads belong to this chat. Home's are loose until the turn claims them. */
  chatId?: string;
  /** Bump to open the model menu from outside — a reply refused as
   *  `model_unavailable` asks the person to pick another. */
  modelMenuRequest?: number;
  autoFocus?: boolean;
  /** What the draft starts as: text shared to the Android app. */
  initialDraft?: string;
  /** Files to attach as if pasted: images shared to the Android app. */
  incomingFiles?: File[];
  /** A temporary chat, or Home about to make one: `/remember` is not offered. */
  temporary?: boolean;
  /** Voice mode. With nothing to send, the send button starts it — called
   *  inside the click, so the page can start audio there. */
  onVoice?: () => void;
  /** The part of a reply the message answers; the page sends it with the
   *  text. */
  quote?: string | null;
  onQuoteClear?: () => void;
  /** A queued message's quote, back with its text for another go. */
  onQuoteRestore?: (quote: string) => void;
  /** Research is on: the + menu turns it on and off, and a chip says so. */
  research?: boolean;
  onResearch?: (on: boolean) => void;
  /** What the empty box says, in place of "Reply" and "Working…": a PC's
   *  thread names the PC. */
  placeholder?: string;
  /** In a PC's Dispatch thread, the PC's name: a model without tools is
   *  told it cannot reach it. */
  pcName?: string;
}

export function Composer({
  variant,
  model,
  onModelChange,
  effort,
  onEffortChange,
  projectId = null,
  onProjectChange,
  context = null,
  suggestion = null,
  question = null,
  onAnswer,
  busy = false,
  onStop,
  onSendNow,
  onSend,
  chatId,
  modelMenuRequest = 0,
  autoFocus = false,
  initialDraft = '',
  incomingFiles,
  temporary = false,
  onVoice,
  quote = null,
  onQuoteClear,
  onQuoteRestore,
  research = false,
  onResearch,
  placeholder: named,
  pcName
}: ComposerProps) {
  const { me, skills, catalogue } = useApp();
  const { navigate } = useRouter();
  const { narrow, touch } = useShell();
  const home = variant === 'home';
  /** The microphone and send: bigger under a finger. */
  const tapSize = touch ? 40 : 36;

  const queued = useQueued(chatId);
  const [draft, setDraft] = useState(initialDraft);
  /** Nothing in the text box, not even an empty code box. */
  const [empty, setEmpty] = useState(!initialDraft);
  const [command, setCommand] = useState<string | null>(null);
  const [files, setFiles] = useState<Pending[]>([]);
  const [menu, setMenu] = useState<MenuName | null>(null);
  const [sending, setSending] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  /** The command menu was shut with Escape or a click elsewhere: it stays
   *  shut until the next keystroke. */
  const [slashShut, setSlashShut] = useState(false);
  const [hiddenAsk, setHiddenAsk] = useState<string | null>(null);
  const [homePrompt] = useState(() => HOME_PROMPTS[Math.floor(Math.random() * HOME_PROMPTS.length)]!);
  const editor = useRef<ComposerEditor | null>(null);
  const mount = useRef<HTMLDivElement>(null);
  /** The draft as the text box last had it, ahead of the next render. */
  const latest = useRef(initialDraft);
  /** The command, ahead of the next render once one is typed. */
  const commandNow = useRef(command);
  commandNow.current = command;
  const readOnly = useRef(false);
  const usageRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const slashList = useRef<HTMLDivElement>(null);
  const removed = useRef(new Set<string>());

  /** A draft from anywhere but typing: into the text box as well. */
  const putDraft = (text: string) => {
    latest.current = text;
    setDraft(text);
    editor.current?.setText(text);
  };

  const dictation = useDictation(
    (text) => {
      const d = latest.current;
      putDraft(d.trim() ? `${d.replace(/\s+$/, '')} ${text}` : text);
      editor.current?.focus(true);
    },
    { live: !!me?.capabilities.voice }
  );
  // Live words show in the draft as they are heard, and are only the draft's
  // once dictation stops.
  const dictating = dictation.state !== 'idle' && !!dictation.heard;
  const shown = dictating ? joinText(draft, dictation.heard) : draft;

  const toggle = (name: MenuName) => setMenu((m) => (m === name ? null : name));
  const close = () => setMenu(null);
  useDismiss(usageRef, menu === 'usage', close);

  useEffect(() => {
    if (modelMenuRequest) setMenu('model');
  }, [modelMenuRequest]);

  const dock = question && question.messageId !== hiddenAsk ? question : null;

  // What the text box calls back with reaches this render's state through
  // these, since the box is made once.
  const typed = useRef<(text: string) => void>(() => {});
  const keyDown = useRef<(e: KeyboardEvent) => boolean>(() => false);
  const pasted = useRef<(files: File[]) => void>(() => {});

  // Made once, on the element React leaves empty for it.
  useLayoutEffect(() => {
    const ed = createEditor(mount.current!, {
      text: latest.current,
      attributes: { class: 'scroll', role: 'textbox', 'aria-multiline': 'true', 'aria-label': home ? 'Message' : 'Reply' },
      editable: () => !readOnly.current,
      onChange: (text, isEmpty, external) => {
        setEmpty(isEmpty);
        if (external) return;
        latest.current = text;
        typed.current(text);
      },
      onKeyDown: (_view, e) => keyDown.current(e),
      onFiles: (files) => pasted.current(files)
    });
    editor.current = ed;
    if (autoFocus) ed.focus(true);
    return () => {
      editor.current = null;
      ed.destroy();
    };
  }, []);

  // Live words go into the box as they are heard, and it is read-only
  // meanwhile. When dictation stops without a result, the draft comes back.
  useEffect(() => {
    const ed = editor.current;
    if (!ed) return;
    readOnly.current = dictating;
    if (dictating) ed.setText(shown);
    else if (ed.text() !== latest.current) ed.setText(latest.current);
    ed.refresh();
  }, [dictating, dictating ? shown : null]);

  const caps = me?.capabilities;
  // Research searches the web, needs a paid plan, and a model that takes tools.
  const canResearch =
    !!onResearch && !!caps?.webSearch && !!me && me.plan !== 'free' && !!catalogue?.models.find((m) => m.id === model)?.tools;
  const canSchedule = !!me && (MAX_AUTOMATIONS[me.plan] ?? 0) > 0 && !temporary && !!catalogue?.models.find((m) => m.id === model)?.tools;
  const canVideo = !!caps?.image && !!me && (MAX_VIDEOS_PER_DAY[me.plan] ?? 0) > 0;
  const offered = useMemo<{ name: string; icon: string; label: string; placeholder: string }[]>(() => {
    const own = (skills ?? []).filter((s) => s.enabled).sort((a, b) => a.name.localeCompare(b.name));
    const ownNames = new Set(own.map((s) => s.name));
    return [
      ...SLASH.filter(
        (s) =>
          (s.needs !== 'image' || !!caps?.image) &&
          (s.needs !== 'video' || canVideo) &&
          (s.needs !== 'automations' || canSchedule) &&
          !(temporary && s.name === 'remember')
      ),
      // Writing, then code, then work. A skill of their own with the same name
      // runs instead, so it stands in the built-in's place below.
      ...BUILTIN_SKILLS.filter((b) => !ownNames.has(b.name)).map((b) => ({
        name: b.name,
        icon: GROUP_ICON[b.group],
        label: b.description,
        placeholder: b.placeholder
      })),
      ...own.map((s) => ({ name: s.name, icon: 'lightning', label: s.description, placeholder: SKILL_PLACEHOLDER }))
    ];
  }, [caps?.image, canVideo, canSchedule, temporary, skills]);
  const slashItems = useMemo(
    () =>
      !command && draft.startsWith('/') && !/\s/.test(draft)
        ? offered.filter((s) => `/${s.name}`.startsWith(draft))
        : [],
    [command, draft, offered]
  );
  const slashOpen = slashItems.length > 0 && !slashShut;
  useDismiss(slashList, slashOpen, () => setSlashShut(true));

  // A quote picked from a reply is answered here.
  useEffect(() => {
    if (quote) editor.current?.focus(true);
  }, [quote]);

  // The arrow keys keep the picked command in view once the list scrolls.
  useLayoutEffect(() => {
    const list = slashList.current;
    const item = list?.children[slashIndex] as HTMLElement | undefined;
    if (!list || !item) return;
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [slashIndex]);

  const listening = dictation.state === 'listening';
  const failedUpload = files.some((f) => f.state === 'failed');
  const hasText = draft.trim().length > 0;
  // Files alone are enough: "what is in this?" can be the file itself. So is
  // `/summarise` alone, which summarises the chat, and a skill alone.
  const alone = command === 'summarise' || (!!command && !SLASH.some((s) => s.name === command));
  const hasContent = hasText || files.length > 0 || alone;
  const canSend = hasContent && !failedUpload && !sending && dictation.state === 'idle';
  const showTab = !home && !!suggestion && !command && !draft && !busy && dictation.state === 'idle';

  const placeholder = listening
    ? 'Listening…'
    : dictation.failed
      ? dictation.failed
      : command
        ? (offered.find((s) => s.name === command)?.placeholder ?? SKILL_PLACEHOLDER)
        : home
          ? homePrompt
          : named
            ? named
            : busy
              ? 'Working…'
              : (!touch && suggestion) || 'Reply';

  const focusEnd = () => {
    requestAnimationFrame(() => editor.current?.focus(true));
  };

  const setText = (text: string) => {
    putDraft(text);
    setSlashIndex(0);
    focusEnd();
  };

  const pickCommand = (name: string, text: string) => {
    setCommand(name);
    setText(text);
  };

  const dropCommand = () => {
    setCommand(null);
    editor.current?.focus();
  };

  typed.current = (text: string) => {
    if (dictation.failed) dictation.clearFailed();
    if (menu === 'attach') close();
    setSlashIndex(0);
    setSlashShut(false);
    const ed = editor.current;
    const line = ed && !commandNow.current ? ed.firstLine() : null;
    const lead = line !== null && ed!.multiline() ? `${line}\n` : line;
    const hit = lead === null ? null : leadingCommand(lead, offered.map((s) => s.name));
    if (hit) {
      // Taking it off the text calls back here with what is left; the cursor
      // stays where it was in that.
      commandNow.current = hit.command;
      setCommand(hit.command);
      ed!.cutStart(lead!.length - hit.rest.length);
      return;
    }
    setDraft(text);
  };

  const addFiles = (picked: File[]) => {
    const parent = chatId ? { chatId } : {};
    const next: Pending[] = picked.map((file) => {
      const key = `f${++keySeq}`;
      const base = { key, name: file.name, mime: file.type || 'application/octet-stream', fileId: null };
      if (file.size > MAX_FILE_BYTES) {
        return { ...base, state: 'failed', error: 'Larger than 10 MB.', done: Promise.resolve(null) };
      }
      const done = uploadFile(file, parent)
        .then((info) => {
          if (removed.current.has(key)) {
            void deleteFile(info.id).catch(() => {});
            return null;
          }
          setFiles((list) => list.map((f) => (f.key === key ? { ...f, state: 'ready', fileId: info.id } : f)));
          return { fileId: info.id, name: info.name, mime: info.mime, size: info.size };
        })
        .catch((err: unknown) => {
          const error =
            err instanceof ApiError && err.code === 'file_too_large' ? 'Larger than 10 MB.' : 'Could not upload this file.';
          setFiles((list) => list.map((f) => (f.key === key ? { ...f, state: 'failed', error } : f)));
          return null;
        });
      return { ...base, state: 'uploading', done };
    });
    setFiles((list) => [...list, ...next]);
    focusEnd();
  };

  // Pasted or dropped files upload as picked ones do, where uploads work.
  pasted.current = (picked: File[]) => {
    if (caps?.files && picked.length) addFiles(picked);
  };
  useEffect(() => {
    if (incomingFiles?.length) pasted.current(incomingFiles);
  }, [incomingFiles]);
  const [dropping, setDropping] = useState(false);
  const carriesFiles = (e: React.DragEvent) => !!caps?.files && Array.from(e.dataTransfer.types).includes('Files');

  // Whether the desktop app offers computer use, as its tools change.
  const [computerOn, setComputerOn] = useState(false);
  useEffect(() => {
    if (!inDesktop()) return;
    let live = true;
    const load = () => void desktopTools().then((tools) => live && setComputerOn(offersComputer(tools)));
    load();
    const off = onDesktopChange(load);
    return () => {
      live = false;
      off();
    };
  }, []);

  /** What attached images the model will not see, said once; otherwise that
   *  it cannot see the screen while computer use is on. */
  const current = catalogue?.models.find((m) => m.id === model);
  const unseen = computerNotice(current, computerOn);
  const unreachable = pcNotice(current, pcName);
  const notice =
    imageNotice(files, current) ??
    (unreachable ? { text: unreachable, switchModel: true } : unseen ? { text: unseen, switchModel: true } : null);

  /** The queued message, back in the box: its text ahead of anything typed
   *  since, its files and its quote. */
  const unqueue = () => {
    const q = chatId ? takeQueued(chatId) : null;
    if (!q) return;
    putDraft([q.text, latest.current].filter((t) => t.trim()).join('\n\n'));
    setFiles((list) => [
      ...q.attachments.map(
        (a): Pending => ({ key: `f${++keySeq}`, name: a.name, mime: a.mime, state: 'ready', fileId: a.fileId, done: Promise.resolve(a) })
      ),
      ...list
    ]);
    if (q.quote) onQuoteRestore?.(q.quote);
    focusEnd();
  };

  /** Stop, and what was queued comes back to the box rather than going. */
  const stop = () => {
    onStop?.();
    unqueue();
  };

  const removeFile = (f: Pending) => {
    removed.current.add(f.key);
    setFiles((list) => list.filter((x) => x.key !== f.key));
    // A loose upload nobody will send is deleted rather than left behind.
    if (f.fileId) void deleteFile(f.fileId).catch(() => {});
  };

  const send = async () => {
    const text = commandText(command, draft);
    if (!canSend) return;
    setSending(true);
    close();
    try {
      const attached = await Promise.all(files.map((f) => f.done));
      if (attached.some((a) => a === null)) return;
      await onSend(text, attached as Attachment[]);
      putDraft('');
      setCommand(null);
      setFiles([]);
    } catch {
      // The page says what went wrong; the draft stays for another go.
    } finally {
      setSending(false);
    }
  };

  // On a touch keyboard there is no Shift, so Enter is a new line there and
  // sending is the button's job.
  const enterSends = useMemo(() => !window.matchMedia?.('(pointer: coarse)').matches, []);

  /**
   * The dock's answers go as they are; the draft and any files stay put. The
   * dock takes the focus with it when it goes, so the text box has it back —
   * except on a touch screen, where that would raise the keyboard.
   */
  const answer = async (picks: string[]) => {
    if (sending || busy || !onAnswer) return;
    try {
      await onAnswer(picks);
    } catch {
      // The page says what went wrong.
    }
    if (enterSends) editor.current?.focus();
  };

  const hideDock = () => {
    if (dock) setHiddenAsk(dock.messageId);
    if (enterSends) editor.current?.focus();
  };

  /** First say on every key in the text box; true takes the key. Anything
   *  left goes to the editor's own keys. */
  keyDown.current = (e: KeyboardEvent): boolean => {
    if (e.isComposing) return false;

    if (command && e.key === 'Backspace' && editor.current?.atStart()) {
      setCommand(null);
      return true;
    }

    if (e.key === 'Escape' && slashOpen) {
      setSlashShut(true);
      return true;
    }

    if (e.key === 'Escape' && dock && !menu) {
      hideDock();
      return true;
    }

    if (slashOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setSlashIndex((i) => (i + step + slashItems.length) % slashItems.length);
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const item = slashItems[Math.min(slashIndex, slashItems.length - 1)];
        if (item) pickCommand(item.name, '');
        return true;
      }
    }

    if (e.key === 'Tab' && !e.shiftKey && showTab && suggestion) {
      setText(suggestion);
      return true;
    }

    // A line that is only a fence opens a code block rather than sending.
    if (e.key === 'Enter' && !e.shiftKey && enterSends && !editor.current?.opensBlock()) {
      void send();
      return true;
    }
    return false;
  };

  const micShown = (!!caps?.transcribe || !!caps?.voice) && dictation.supported;

  const sendButton = (
    <button
      type="button"
      className="bare"
      aria-label="Send"
      title="Send"
      disabled={!canSend}
      onClick={() => void send()}
      style={{
        width: tapSize,
        height: tapSize,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--radius-sm)',
        background: hasContent ? 'var(--primary)' : 'var(--app-panel-sunken)',
        color: hasContent ? 'var(--text-on-primary)' : 'var(--text-faint)',
        cursor: canSend ? 'pointer' : 'default'
      }}
    >
      {sending ? <Ph name="circle-notch" size={17} className="spin" /> : <Ph name="paper-plane-tilt" size={17} />}
    </button>
  );

  return (
    <div style={{ maxWidth: home ? 700 : 712, margin: '0 auto', position: 'relative' }}>
      {slashOpen ? (
        <div
          ref={slashList}
          role="listbox"
          aria-label="Commands"
          className="fade-in"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'calc(100% + 8px)',
            zIndex: 10,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-app-card)',
            boxShadow: 'var(--shadow-lg)',
            padding: 5,
            maxHeight: 'min(260px, 30vh)',
            overflowY: 'auto'
          }}
        >
          {slashItems.map((k, i) => (
            <button
              key={k.name}
              type="button"
              role="option"
              aria-selected={i === slashIndex}
              className="bare hov-row"
              onClick={() => pickCommand(k.name, '')}
              style={{
                width: '100%',
                height: 36,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '0 10px',
                borderRadius: 6,
                background: i === slashIndex ? 'var(--border-subtle)' : 'transparent'
              }}
            >
              <Ph name={k.icon} size={15} color="var(--text-muted)" />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)', minWidth: 92, flex: 'none' }}>
                /{k.name}
              </span>
              <span style={{ minWidth: 0, fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {k.label}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {menu === 'usage' && me ? (
        <UsagePopover
          ref={usageRef}
          context={context}
          usage={me.usage}
          onDetails={() => {
            close();
            navigate('/settings/usage');
          }}
        />
      ) : null}

      {dock ? (
        <QuestionDock
          key={dock.messageId}
          ref={dockRef}
          ask={dock.ask}
          onAnswer={(picks) => void answer(picks)}
          onWrite={() => editor.current?.focus()}
          onHide={hideDock}
        />
      ) : null}

      <div
        onDragOver={(e) => {
          if (!carriesFiles(e)) return;
          e.preventDefault();
          setDropping(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false);
        }}
        onDrop={(e) => {
          setDropping(false);
          if (!carriesFiles(e)) return;
          e.preventDefault();
          pasted.current(Array.from(e.dataTransfer.files));
        }}
        style={{
          background: 'var(--surface-card)',
          border: `1px solid ${dropping ? 'var(--primary)' : 'var(--border-subtle)'}`,
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          padding: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }}
      >
        {queued ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 0 8px', fontSize: 13 }}>
            <span style={{ flex: 'none', color: 'var(--text-faint)' }}>Queued</span>
            <button
              type="button"
              className="bare"
              title="Edit"
              onClick={unqueue}
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'left',
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {queued.text.trim() || queued.attachments.map((a) => a.name).join(', ')}
            </button>
            {onSendNow ? (
              <button
                type="button"
                className="bare hov-soft"
                onClick={onSendNow}
                style={{ flex: 'none', height: 26, padding: '0 10px', borderRadius: 6, color: 'var(--text-strong)' }}
              >
                Send now
              </button>
            ) : null}
            <button
              type="button"
              className="bare hov-soft"
              aria-label="Remove queued message"
              title="Remove"
              onClick={() => chatId && takeQueued(chatId)}
              style={{
                width: 24,
                height: 24,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 6,
                color: 'var(--text-muted)'
              }}
            >
              <Ph name="x" size={13} />
            </button>
          </div>
        ) : null}

        {quote ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 4px 0 8px' }}>
            <QuoteLines text={quote} lines={2} style={{ flex: 1, minWidth: 0, fontSize: 13 }} />
            <button
              type="button"
              className="bare hov-soft"
              aria-label="Remove quote"
              title="Remove quote"
              onClick={() => {
                onQuoteClear?.();
                editor.current?.focus();
              }}
              style={{
                width: 24,
                height: 24,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 6,
                color: 'var(--text-muted)'
              }}
            >
              <Ph name="x" size={13} />
            </button>
          </div>
        ) : null}

        {files.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '2px 4px 0' }}>
            {files.map((f) => (
              <AttachmentChip
                key={f.key}
                name={f.name}
                mime={f.mime}
                fileId={f.fileId}
                state={f.state}
                error={f.error}
                onRemove={() => removeFile(f)}
              />
            ))}
          </div>
        ) : null}

        {notice ? (
          <div
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 8, padding: '0 4px 0 8px', fontSize: 13, color: 'var(--text-muted)' }}
          >
            <span>{notice.text}</span>
            {notice.switchModel ? (
              <button
                type="button"
                className="bare"
                onClick={() => setMenu('model')}
                style={{ fontSize: 13, color: 'var(--text-link)', whiteSpace: 'nowrap' }}
              >
                Choose another model
              </button>
            ) : null}
          </div>
        ) : null}

        {showTab && touch && suggestion ? (
          <div style={{ display: 'flex', padding: '2px 4px 0' }}>
            <button
              type="button"
              className="bare hov-soft"
              onClick={() => putDraft(suggestion)}
              style={{
                minWidth: 0,
                padding: '8px 14px',
                borderRadius: 999,
                border: '1px solid var(--border-strong)',
                fontSize: 13,
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {suggestion}
            </button>
          </div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {command ? <CommandChip command={command} onRemove={dropCommand} style={{ margin: '8px 0 0 6px' }} /> : null}
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            {empty ? (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  padding: command ? '8px 8px 0 0' : '8px 8px 0',
                  fontSize: 15,
                  lineHeight: 1.5,
                  color: 'var(--text-faint)',
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}
              >
                {placeholder}
              </div>
            ) : null}
            {/* The editor's own element: React draws it and never its
                contents. Grows with the text up to MAX_INPUT, then scrolls. */}
            <div
              ref={mount}
              aria-placeholder={placeholder}
              style={{
                minHeight: (home ? (narrow ? 2 : 3) : 1) * LINE + 8,
                maxHeight: MAX_INPUT,
                overflowY: 'auto',
                padding: command ? '8px 8px 0 0' : '8px 8px 0',
                fontSize: 15,
                lineHeight: 1.5,
                color: 'var(--text-strong)',
                outline: 'none'
              }}
            />
          </div>
          {showTab && !touch ? (
            <span
              aria-hidden="true"
              style={{
                flex: 'none',
                margin: '9px 6px 0 0',
                height: 20,
                display: 'flex',
                alignItems: 'center',
                padding: '0 6px',
                borderRadius: 5,
                border: '1px solid var(--border-strong)',
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: 'var(--text-faint)'
              }}
            >
              Tab
            </span>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: narrow ? 6 : 2, minWidth: 0 }}>
          <AttachMenu
            open={menu === 'attach'}
            onToggle={() => toggle('attach')}
            onClose={close}
            canUpload={!!caps?.files}
            canImage={!!caps?.image}
            onFiles={addFiles}
            onImagePrompt={() => pickCommand('image', draft)}
            canResearch={canResearch}
            research={research}
            onResearch={() => onResearch?.(!research)}
          />

          {/* The Auto/Manual menu sits here in the prototype. It decides when
              Assist asks before acting in a connected app, and the web app has
              no connectors yet, so it stays out until one is live. */}

          {research && canResearch ? (
            <button
              type="button"
              className="bare hov-soft"
              aria-label="Turn Research off"
              title="Turn Research off"
              onClick={() => onResearch?.(false)}
              style={{
                flex: 'none',
                height: 30,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: narrow ? '0 8px' : '0 10px',
                borderRadius: 999,
                background: 'var(--primary-soft)',
                color: 'var(--primary-text)',
                fontSize: 13
              }}
            >
              <Ph name="magnifying-glass" size={14} />
              {narrow ? null : 'Research'}
              <Ph name="x" size={11} />
            </button>
          ) : null}

          {home && onProjectChange ? (
            <ProjectPill
              value={projectId}
              onChange={onProjectChange}
              open={menu === 'project'}
              onToggle={() => toggle('project')}
              onClose={close}
              iconOnly={narrow}
              touch={touch}
            />
          ) : null}

          <span style={{ flex: 1 }} />

          <ModelMenu
            value={model}
            onPick={onModelChange}
            open={menu === 'model'}
            onToggle={() => toggle('model')}
            onClose={close}
            effort={narrow ? { value: effort, onPick: onEffortChange } : undefined}
          />

          {narrow ? null : (
            <EffortMenu
              model={model}
              value={effort}
              onPick={onEffortChange}
              open={menu === 'effort'}
              onToggle={() => toggle('effort')}
              onClose={close}
            />
          )}

          {!home ? <UsageRing context={context} open={menu === 'usage'} onToggle={() => toggle('usage')} /> : null}

          {micShown ? (
            <button
              type="button"
              className="bare hov-strong"
              aria-label={listening ? 'Stop dictating' : 'Dictate'}
              title={listening ? 'Stop dictating' : 'Dictate'}
              aria-pressed={listening}
              disabled={dictation.state === 'transcribing'}
              onClick={dictation.toggle}
              style={{
                width: tapSize,
                height: tapSize,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-sm)',
                color: listening ? 'var(--primary)' : 'var(--text-muted)',
                background: listening ? 'var(--primary-soft)' : 'transparent'
              }}
            >
              {dictation.state === 'transcribing' ? (
                <Ph name="circle-notch" size={18} className="spin" />
              ) : (
                <Ph name="microphone" size={18} />
              )}
            </button>
          ) : null}

          {busy && hasContent ? sendButton : null}
          {busy ? (
            <button
              type="button"
              className="bare"
              aria-label="Stop"
              title="Stop"
              onClick={stop}
              style={{
                width: tapSize,
                height: tapSize,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--primary)',
                color: 'var(--text-on-primary)'
              }}
            >
              <Ph name="stop" fill size={15} />
            </button>
          ) : onVoice && caps?.voice && !hasContent && dictation.state === 'idle' ? (
            <button
              type="button"
              className="bare"
              aria-label="Voice"
              title="Voice"
              onClick={onVoice}
              style={{
                width: tapSize,
                height: tapSize,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--primary)',
                color: 'var(--text-on-primary)'
              }}
            >
              <Ph name="waveform" size={18} />
            </button>
          ) : (
            sendButton
          )}
        </div>
      </div>
    </div>
  );
}
